/* @flow */

import * as React from 'react';
import { ThemeProvider } from 'react-jss';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import jss from 'jss';
import preset from 'jss-preset-default';
import { configure } from 'react-hotkeys';

import styles from 'src/server/app/style/base.style';
import useComponentPreload from './hook/useComponentPreload';
import useInitialPayloadFetching from './hook/useInitialPayloadFetching';
import useJSClass from './hook/useJSClass';
import useServerStylesRemoval from './hook/useServerStylesRemoval';
import useTimeFormatPatch from './hook/useTimeFormatPatch';
import useSetAppTimezone from './hook/useSetAppTimezone';
import useTouchDetection from './hook/useTouchDetection';
import useUserReportingRegistration from './hook/useUserReportingRegistration';
import useValidationMessages from './hook/useValidationMessages';
import HTTPStatusCodeSwitch from './switch/HTTPStatusCodeSwitch';
import RedirectSwitch from './switch/RedirectSwitch';
import TemplateSwitch from './switch/TemplateSwitch';
import HelpSwitch from './switch/HelpSwitch';
import useMobileKeyboardVisibility from 'src/shared/app/base/hook/useMobileKeyboardVisibility';
import useMounted from 'src/shared/app/base/hook/useMounted';
import ModalCenter from 'src/shared/app/base/modal/component/ModalCenter';
import useLiveChatVisitorAuth from 'plugin/live-chat/hook/useLiveChatVisitorAuth';
import useRouteRegistration from 'src/shared/app/base/route/hook/useRouteRegistration';
import usePageTracking from 'src/shared/app/base/tracking/hook/usePageTracking';
import useTrackAbandons from 'src/shared/app/base/tracking/hook/useTrackAbandons';
import useTTITracking from 'src/shared/app/base/tracking/hook/useTTITracking';
import useFeatureDecisions from 'src/shared/app/feature/hook/useFeatureDecisions';
import useFeatureRouter from 'src/shared/app/feature/hook/useFeatureRouter';
import useGlobalNotifications from 'src/shared/app/notification/hook/useGlobalNotifications';
import TooltipProvider from 'src/shared/ui/component/misc/TooltipProvider';
import useOauthMessages from 'src/shared/app/account/oauth/hook/useOauthMessages';
import useMuiCreateTheme from 'src/shared/app/base/style/hook/useMuiCreateTheme';
import ModalStatusProvider from 'src/shared/app/base/modal/component/ModalStatusProvider';
import { SSRProvider } from 'react-aria';

import useCreateBaseStyleProps from 'src/shared/app/base/hook/useCreateBaseStyleProps';
import DocumentTitle from 'src/shared/app/base/component/document/DocumentTitle';
import { ThemeProvider as ThemeProviderMui } from '@mui/styles';

jss.setup(preset());
const sheet = jss.createStyleSheet(styles, { link: true }).attach();

configure({
  stopEventPropagationAfterIgnoring: false,
});

const App = (): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  // Initialize feature router
  useFeatureRouter();

  // Routes
  useRouteRegistration();

  // Calls to API
  useInitialPayloadFetching();
  useTimeFormatPatch();

  // Websockets
  useGlobalNotifications();

  // OauthMessages
  useOauthMessages();

  // DOM manipulation
  useJSClass();
  useTouchDetection();
  useServerStylesRemoval();

  // Preload
  useComponentPreload();

  // Tracking
  usePageTracking();
  useTrackAbandons();
  useTTITracking();

  // Libraries/Services configuration
  useUserReportingRegistration();
  useValidationMessages();
  useSetAppTimezone();

  // Plugins
  useLiveChatVisitorAuth();

  const { isHelpEnabled } = useFeatureDecisions();
  const isMounted = useMounted();
  const isMobileKeyboardVisible = useMobileKeyboardVisibility();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const helpSwitch = isHelpEnabled && isMounted && !isMobileKeyboardVisible && (
    <HelpSwitch />
  );
  const modalCenter = isMounted && <ModalCenter />;

  const stylesProps = useCreateBaseStyleProps();

  sheet.update(stylesProps);

  const { theme } = stylesProps;

  const MuiTheme = useMuiCreateTheme(theme);

  return (
    <React.Fragment>
      <ThemeProvider theme={theme}>
        <ThemeProviderMui theme={MuiTheme}>
          <EmotionThemeProvider theme={MuiTheme}>
            <ModalStatusProvider>
              <SSRProvider>
                <DocumentTitle />
                <RedirectSwitch />
                <HTTPStatusCodeSwitch />
                <TemplateSwitch />
                {helpSwitch}

                {modalCenter}
                <TooltipProvider />
              </SSRProvider>
            </ModalStatusProvider>
          </EmotionThemeProvider>
        </ThemeProviderMui>
      </ThemeProvider>
    </React.Fragment>
  );
};

export default React.memo<{}>(App);
